import React from 'react';
import { ModuleId } from '../../types';
import { MetricStairsCompact } from '../visuals/MetricStairsCompact';
import { FractionPizza } from '../visuals/FractionPizza';
import { RuleCard } from '../visuals/RuleCard';
import { motion } from 'framer-motion';
interface ModuleCheatSheetContentProps {
  moduleId: ModuleId;
}
export function ModuleCheatSheetContent({
  moduleId
}: ModuleCheatSheetContentProps) {
  switch (moduleId) {
    case 'Metriek':
      return <>
          <div className="mb-6" data-id="element-537">
            <h3 className="text-2xl font-bold text-slate-900 mb-2 flex items-center gap-2" data-id="element-538">
              <span className="text-3xl" data-id="element-539">📏</span>
              Metriek & Meetkunde
            </h3>
            <p className="text-slate-600" data-id="element-540">
              Alles over meten en rekenen met vormen
            </p>
          </div>

          <div className="space-y-6" data-id="element-541">
            {/* Section 1: Lengtematen - Interactive */}
            <motion.div whileHover={{
            scale: 1.01
          }} className="bg-indigo-50 rounded-xl p-5 border-2 border-indigo-200 shadow-sm transition-all" data-id="element-542">
              <h4 className="font-bold text-indigo-900 mb-3 text-lg flex items-center gap-2" data-id="element-543">
                📐 Lengtematen Omrekenen
              </h4>
              <MetricStairsCompact type="length" showAnimation={true} data-id="element-544" />
            </motion.div>

            {/* Section 2: Inhoudsmaten - Interactive */}
            <motion.div whileHover={{
            scale: 1.01
          }} className="bg-blue-50 rounded-xl p-5 border-2 border-blue-200 shadow-sm transition-all" data-id="element-545">
              <h4 className="font-bold text-blue-900 mb-3 text-lg flex items-center gap-2" data-id="element-546">
                🧃 Inhoudsmaten (Liters)
              </h4>
              <MetricStairsCompact type="volume" showAnimation={true} data-id="element-547" />

              {/* Kubieke Meters Visual Card */}
              <div className="mt-4 bg-white rounded-lg p-4 border-2 border-blue-300 shadow-sm" data-id="element-548">
                <h5 className="font-bold text-blue-900 mb-2 text-sm flex items-center gap-2" data-id="element-549">
                  📦 Kubieke Meters (m³)
                </h5>
                <div className="flex items-center justify-between text-sm" data-id="element-550">
                  <span className="text-slate-700" data-id="element-551">1 m³ (kuub)</span>
                  <span className="text-2xl" data-id="element-552">💧</span>
                  <span className="font-bold text-blue-900" data-id="element-553">= 1.000 liter</span>
                </div>
              </div>
            </motion.div>

            {/* Section 3: Formules - Visual Cards */}
            <div className="bg-emerald-50 rounded-xl p-5 border-2 border-emerald-200" data-id="element-554">
              <h4 className="font-bold text-emerald-900 mb-3 text-lg" data-id="element-555">
                📊 Formules
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-556">
                <div className="bg-white rounded-lg p-3 border border-emerald-200 shadow-sm hover:shadow-md transition-shadow" data-id="element-557">
                  <p className="font-bold text-emerald-900 mb-1 flex items-center gap-2" data-id="element-558">
                    🔄 Omtrek
                  </p>
                  <p className="text-xs text-slate-500 mb-1" data-id="element-559">Eromheen lopen</p>
                  <p className="text-sm font-mono bg-emerald-50 p-1 rounded text-center" data-id="element-560">
                    2 × (lengte + breedte)
                  </p>
                </div>

                <div className="bg-white rounded-lg p-3 border border-emerald-200 shadow-sm hover:shadow-md transition-shadow" data-id="element-561">
                  <p className="font-bold text-emerald-900 mb-1 flex items-center gap-2" data-id="element-562">
                    ⬛ Oppervlakte
                  </p>
                  <p className="text-xs text-slate-500 mb-1" data-id="element-563">Vloer bedekken</p>
                  <p className="text-sm font-mono bg-emerald-50 p-1 rounded text-center" data-id="element-564">
                    lengte × breedte
                  </p>
                </div>

                <div className="bg-white rounded-lg p-3 border border-emerald-200 shadow-sm hover:shadow-md transition-shadow sm:col-span-2" data-id="element-565">
                  <p className="font-bold text-emerald-900 mb-1 flex items-center gap-2" data-id="element-566">
                    🧊 Inhoud
                  </p>
                  <p className="text-xs text-slate-500 mb-1" data-id="element-567">
                    Vullen met water
                  </p>
                  <p className="text-sm font-mono bg-emerald-50 p-1 rounded text-center" data-id="element-568">
                    lengte × breedte × hoogte
                  </p>
                </div>
              </div>
            </div>

            <RuleCard title="Super Handig!" content="Omtrek = hek (eromheen). Oppervlakte = tegels (erop). Inhoud = water (erin)." icon="lightbulb" data-id="element-569" />
          </div>
        </>;
    case 'Getalbegrip':
      return <>
          <div className="mb-6" data-id="element-570">
            <h3 className="text-2xl font-bold text-slate-900 mb-2 flex items-center gap-2" data-id="element-571">
              <span className="text-3xl" data-id="element-572">🔢</span>
              Getalbegrip & Breuken
            </h3>
            <p className="text-slate-600" data-id="element-573">
              Alles over getallen, breuken en rekenen
            </p>
          </div>

          <div className="space-y-6" data-id="element-574">
            {/* Kolomsgewijs Aftrekken - Visual Steps */}
            <div className="bg-red-50 rounded-xl p-5 border-2 border-red-200" data-id="element-575">
              <h4 className="font-bold text-red-900 mb-3 text-lg" data-id="element-576">
                ➖ Kolomsgewijs Aftrekken
              </h4>
              <div className="flex flex-col gap-3" data-id="element-577">
                <div className="flex items-start gap-3 bg-white p-3 rounded-lg border border-red-100" data-id="element-578">
                  <div className="bg-red-100 text-red-700 font-bold w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-579">
                    1
                  </div>
                  <div data-id="element-580">
                    <p className="font-bold text-red-900 text-sm" data-id="element-581">
                      Begin rechts
                    </p>
                    <p className="text-xs text-slate-600" data-id="element-582">
                      Eerst eenheden, dan tientallen.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3 bg-white p-3 rounded-lg border border-red-100" data-id="element-583">
                  <div className="bg-red-100 text-red-700 font-bold w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-584">
                    2
                  </div>
                  <div data-id="element-585">
                    <p className="font-bold text-red-900 text-sm" data-id="element-586">
                      Lenen als het moet
                    </p>
                    <p className="text-xs text-slate-600" data-id="element-587">
                      Bovenste kleiner? Leen 1 van de buurman (is 10 waard!).
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Consolidated Breuken Section */}
            <div className="bg-amber-50 rounded-xl p-5 border-2 border-amber-200" data-id="element-588">
              <h4 className="font-bold text-amber-900 mb-4 text-lg flex items-center gap-2" data-id="element-589">
                🍕 Alles over Breuken
              </h4>

              <div className="space-y-6" data-id="element-590">
                {/* Visual Explanation */}
                <div className="bg-white rounded-xl p-4 border border-amber-200 shadow-sm" data-id="element-591">
                  <div className="flex flex-col sm:flex-row items-center gap-6" data-id="element-592">
                    <FractionPizza numerator={3} denominator={4} label="Driekwart (3/4)" type="pizza" data-id="element-593" />
                    <div className="flex-1 space-y-2 w-full" data-id="element-594">
                      <div className="flex items-center justify-between bg-amber-50 p-2 rounded" data-id="element-595">
                        <span className="text-sm font-bold text-amber-900" data-id="element-596">
                          Teller (boven)
                        </span>
                        <span className="text-xs text-amber-700" data-id="element-597">
                          Hoeveel stukken je hebt
                        </span>
                      </div>
                      <div className="flex items-center justify-between bg-amber-50 p-2 rounded" data-id="element-598">
                        <span className="text-sm font-bold text-amber-900" data-id="element-599">
                          Noemer (onder)
                        </span>
                        <span className="text-xs text-amber-700" data-id="element-600">
                          Totaal aantal stukken
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Comparison Logic Cards */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-601">
                  <div className="bg-white p-3 rounded-lg border border-amber-200" data-id="element-602">
                    <p className="text-xs font-bold text-amber-900 uppercase mb-1" data-id="element-603">
                      Zelfde onderkant?
                    </p>
                    <p className="text-sm text-slate-700" data-id="element-604">
                      Kijk naar boven! <br data-id="element-605" />
                      <strong data-id="element-606">3</strong>/5 &gt; <strong data-id="element-607">2</strong>/5
                    </p>
                  </div>
                  <div className="bg-white p-3 rounded-lg border border-amber-200" data-id="element-608">
                    <p className="text-xs font-bold text-amber-900 uppercase mb-1" data-id="element-609">
                      Zelfde bovenkant?
                    </p>
                    <p className="text-sm text-slate-700" data-id="element-610">
                      Kleine onderkant wint! <br data-id="element-611" />
                      1/<strong data-id="element-612">4</strong> &lt; 1/<strong data-id="element-613">3</strong>
                    </p>
                  </div>
                </div>

                {/* Operations */}
                <div className="bg-orange-100 rounded-lg p-4 border border-orange-200" data-id="element-614">
                  <h5 className="font-bold text-orange-900 mb-2 text-sm" data-id="element-615">
                    ➕ Optellen
                  </h5>
                  <p className="text-sm text-slate-700 mb-2" data-id="element-616">
                    Alleen de bovenkant optellen! De onderkant blijft gelijk.
                  </p>
                  <div className="bg-white px-3 py-2 rounded text-center font-mono text-orange-800 font-bold" data-id="element-617">
                    1/4 + 2/4 = 3/4
                  </div>
                </div>
              </div>
            </div>

            {/* Percentages Grid */}
            <div className="bg-indigo-50 rounded-xl p-5 border-2 border-indigo-200" data-id="element-618">
              <h4 className="font-bold text-indigo-900 mb-3 text-lg" data-id="element-619">
                📊 Percentages
              </h4>
              <div className="grid grid-cols-2 gap-3" data-id="element-620">
                <div className="bg-white p-3 rounded-lg text-center border border-indigo-100" data-id="element-621">
                  <div className="text-xl font-bold text-indigo-600" data-id="element-622">50%</div>
                  <div className="text-xs text-slate-500" data-id="element-623">de helft (1/2)</div>
                </div>
                <div className="bg-white p-3 rounded-lg text-center border border-indigo-100" data-id="element-624">
                  <div className="text-xl font-bold text-indigo-600" data-id="element-625">25%</div>
                  <div className="text-xs text-slate-500" data-id="element-626">een kwart (1/4)</div>
                </div>
                <div className="bg-white p-3 rounded-lg text-center border border-indigo-100" data-id="element-627">
                  <div className="text-xl font-bold text-indigo-600" data-id="element-628">75%</div>
                  <div className="text-xs text-slate-500" data-id="element-629">driekwart (3/4)</div>
                </div>
                <div className="bg-white p-3 rounded-lg text-center border border-indigo-100" data-id="element-630">
                  <div className="text-xl font-bold text-indigo-600" data-id="element-631">10%</div>
                  <div className="text-xs text-slate-500" data-id="element-632">
                    een tiende (1/10)
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>;
    case 'TijdGeld':
      return <>
          <div className="mb-6" data-id="element-633">
            <h3 className="text-2xl font-bold text-slate-900 mb-2 flex items-center gap-2" data-id="element-634">
              <span className="text-3xl" data-id="element-635">⏰</span>
              Tijd & Geld
            </h3>
            <p className="text-slate-600" data-id="element-636">
              Alles over klokkijken en rekenen met geld
            </p>
          </div>

          <div className="space-y-6" data-id="element-637">
            {/* Tijd Omrekenen - Visual Grid */}
            <div className="bg-emerald-50 rounded-xl p-5 border-2 border-emerald-200" data-id="element-638">
              <h4 className="font-bold text-emerald-900 mb-3 text-lg" data-id="element-639">
                🕐 Tijd Omrekenen
              </h4>
              <div className="grid grid-cols-2 gap-3 text-sm" data-id="element-640">
                <div className="bg-white p-2 rounded border border-emerald-100 flex justify-between" data-id="element-641">
                  <span className="font-bold text-emerald-800" data-id="element-642">1 minuut</span>
                  <span data-id="element-643">60 sec</span>
                </div>
                <div className="bg-white p-2 rounded border border-emerald-100 flex justify-between" data-id="element-644">
                  <span className="font-bold text-emerald-800" data-id="element-645">1 uur</span>
                  <span data-id="element-646">60 min</span>
                </div>
                <div className="bg-white p-2 rounded border border-emerald-100 flex justify-between" data-id="element-647">
                  <span className="font-bold text-emerald-800" data-id="element-648">Kwartier</span>
                  <span data-id="element-649">15 min</span>
                </div>
                <div className="bg-white p-2 rounded border border-emerald-100 flex justify-between" data-id="element-650">
                  <span className="font-bold text-emerald-800" data-id="element-651">1 dag</span>
                  <span data-id="element-652">24 uur</span>
                </div>
              </div>
            </div>

            {/* NEW: Tijdsduur Berekenen */}
            <div className="bg-teal-50 rounded-xl p-5 border-2 border-teal-200" data-id="element-653">
              <h4 className="font-bold text-teal-900 mb-3 text-lg" data-id="element-654">
                ⏳ Tijdsduur Berekenen
              </h4>
              <div className="bg-white rounded-lg p-4 border border-teal-200" data-id="element-655">
                <p className="font-bold text-teal-900 mb-2 text-sm" data-id="element-656">
                  Van 14:30 tot 16:45
                </p>
                <div className="space-y-2 text-sm" data-id="element-657">
                  <div className="flex items-center gap-2" data-id="element-658">
                    <span className="bg-teal-100 text-teal-800 px-2 py-0.5 rounded text-xs font-bold" data-id="element-659">
                      Stap 1
                    </span>
                    <span className="text-slate-700" data-id="element-660">
                      Tel hele uren: 14:30 → 16:30 = 2 uur
                    </span>
                  </div>
                  <div className="flex items-center gap-2" data-id="element-661">
                    <span className="bg-teal-100 text-teal-800 px-2 py-0.5 rounded text-xs font-bold" data-id="element-662">
                      Stap 2
                    </span>
                    <span className="text-slate-700" data-id="element-663">
                      Tel minuten: 16:30 → 16:45 = 15 min
                    </span>
                  </div>
                  <div className="mt-2 pt-2 border-t border-teal-100 font-bold text-teal-900" data-id="element-664">
                    Antwoord: 2 uur en 15 minuten
                  </div>
                </div>
              </div>
            </div>

            {/* Klokkijken - Visual */}
            <div className="bg-cyan-50 rounded-xl p-5 border-2 border-cyan-200" data-id="element-665">
              <h4 className="font-bold text-cyan-900 mb-3 text-lg" data-id="element-666">
                ⏱️ Digitale Klok
              </h4>
              <div className="bg-white rounded-lg p-4 border border-cyan-200 text-center" data-id="element-667">
                <div className="text-3xl font-mono font-bold text-slate-800 tracking-wider mb-2" data-id="element-668">
                  14:30:45
                </div>
                <div className="flex justify-center gap-8 text-xs text-slate-500 uppercase tracking-wide" data-id="element-669">
                  <span data-id="element-670">Uur</span>
                  <span data-id="element-671">Min</span>
                  <span data-id="element-672">Sec</span>
                </div>
              </div>
            </div>

            {/* Geld - Visual Cards */}
            <div className="bg-amber-50 rounded-xl p-5 border-2 border-amber-200" data-id="element-673">
              <h4 className="font-bold text-amber-900 mb-3 text-lg" data-id="element-674">
                💰 Geld Rekenen
              </h4>
              <div className="space-y-3" data-id="element-675">
                <div className="bg-white p-3 rounded-lg border border-amber-200 flex items-center gap-3" data-id="element-676">
                  <div className="text-2xl" data-id="element-677">💶</div>
                  <div data-id="element-678">
                    <p className="font-bold text-amber-900 text-sm" data-id="element-679">
                      1 euro = 100 cent
                    </p>
                    <p className="text-xs text-slate-500" data-id="element-680">
                      €2,50 is dus 250 cent
                    </p>
                  </div>
                </div>
                <div className="bg-white p-3 rounded-lg border border-amber-200 flex items-center gap-3" data-id="element-681">
                  <div className="text-2xl" data-id="element-682">🧮</div>
                  <div data-id="element-683">
                    <p className="font-bold text-amber-900 text-sm" data-id="element-684">
                      Teruggeven
                    </p>
                    <p className="text-xs text-slate-500" data-id="element-685">
                      Tel op vanaf het bedrag tot wat je geeft.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* NEW: Wisselgeld Strategie */}
            <div className="bg-yellow-50 rounded-xl p-5 border-2 border-yellow-200" data-id="element-686">
              <h4 className="font-bold text-yellow-900 mb-3 text-lg" data-id="element-687">
                🪙 Wisselgeld Strategie
              </h4>
              <div className="bg-white rounded-lg p-4 border border-yellow-200" data-id="element-688">
                <p className="text-sm text-slate-700 mb-2" data-id="element-689">
                  Je betaalt €10,00 voor iets van €6,35.
                </p>
                <div className="space-y-1 text-xs font-mono bg-yellow-50 p-2 rounded text-slate-700 mb-2" data-id="element-690">
                  <div data-id="element-691">€6,35 + €0,05 = €6,40</div>
                  <div data-id="element-692">€6,40 + €0,10 = €6,50</div>
                  <div data-id="element-693">€6,50 + €0,50 = €7,00</div>
                  <div data-id="element-694">€7,00 + €3,00 = €10,00</div>
                </div>
                <p className="font-bold text-yellow-900 text-sm" data-id="element-695">
                  Wisselgeld: €3,65
                </p>
              </div>
            </div>
          </div>
        </>;
    case 'SpellingWW':
      return <>
          <div className="mb-6" data-id="element-696">
            <h3 className="text-2xl font-bold text-slate-900 mb-2 flex items-center gap-2" data-id="element-697">
              <span className="text-3xl" data-id="element-698">✍️</span>
              Werkwoordspelling
            </h3>
            <p className="text-slate-600" data-id="element-699">Alles over werkwoorden spellen</p>
          </div>

          <div className="space-y-6" data-id="element-700">
            {/* Stam Vinden - Steps */}
            <div className="bg-rose-50 rounded-xl p-5 border-2 border-rose-200" data-id="element-701">
              <h4 className="font-bold text-rose-900 mb-3 text-lg" data-id="element-702">
                🎯 De Stam Vinden
              </h4>
              <div className="flex items-center gap-2 bg-white p-3 rounded-lg border border-rose-100 text-sm" data-id="element-703">
                <span className="font-mono bg-rose-100 px-2 py-1 rounded text-rose-800" data-id="element-704">
                  lopen
                </span>
                <span data-id="element-705">➖</span>
                <span className="font-mono bg-rose-100 px-2 py-1 rounded text-rose-800" data-id="element-706">
                  en
                </span>
                <span data-id="element-707">🟰</span>
                <span className="font-bold text-rose-900" data-id="element-708">loop</span>
              </div>
            </div>

            {/* Tegenwoordige Tijd - Grid */}
            <div className="bg-purple-50 rounded-xl p-5 border-2 border-purple-200" data-id="element-709">
              <h4 className="font-bold text-purple-900 mb-3 text-lg" data-id="element-710">
                📝 Tegenwoordige Tijd
              </h4>
              <div className="grid grid-cols-1 gap-2 text-sm" data-id="element-711">
                <div className="bg-white p-2 rounded border border-purple-100 flex justify-between items-center" data-id="element-712">
                  <span className="font-bold text-purple-900" data-id="element-713">ik</span>
                  <span className="font-mono text-slate-600" data-id="element-714">stam</span>
                </div>
                <div className="bg-white p-2 rounded border border-purple-100 flex justify-between items-center" data-id="element-715">
                  <span className="font-bold text-purple-900" data-id="element-716">
                    jij (ervoor) / hij / zij
                  </span>
                  <span className="font-mono text-slate-600" data-id="element-717">stam + t</span>
                </div>
                <div className="bg-white p-2 rounded border border-purple-100 flex justify-between items-center" data-id="element-718">
                  <span className="font-bold text-purple-900" data-id="element-719">
                    jij (erachter)
                  </span>
                  <span className="font-mono text-slate-600" data-id="element-720">stam</span>
                </div>
                <div className="bg-white p-2 rounded border border-purple-100 flex justify-between items-center" data-id="element-721">
                  <span className="font-bold text-purple-900" data-id="element-722">
                    wij / jullie / zij
                  </span>
                  <span className="font-mono text-slate-600" data-id="element-723">
                    hele werkwoord
                  </span>
                </div>
              </div>
            </div>

            {/* NEW: Jij-Omkering Uitleg */}
            <div className="bg-fuchsia-50 rounded-xl p-5 border-2 border-fuchsia-200" data-id="element-724">
              <h4 className="font-bold text-fuchsia-900 mb-3 text-lg" data-id="element-725">
                🔄 Jij-Omkering
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-726">
                <div className="bg-white p-3 rounded-lg border border-fuchsia-200" data-id="element-727">
                  <p className="font-bold text-fuchsia-900 text-sm mb-1" data-id="element-728">
                    Jij staat ervoor
                  </p>
                  <p className="text-xs text-slate-600 mb-1" data-id="element-729">Grondwoord + t</p>
                  <p className="font-mono text-xs bg-fuchsia-50 p-1 rounded text-center" data-id="element-730">
                    Jij loop
                    <span className="font-bold text-fuchsia-700" data-id="element-731">t</span>
                  </p>
                </div>
                <div className="bg-white p-3 rounded-lg border border-fuchsia-200" data-id="element-732">
                  <p className="font-bold text-fuchsia-900 text-sm mb-1" data-id="element-733">
                    Jij staat erachter
                  </p>
                  <p className="text-xs text-slate-600 mb-1" data-id="element-734">
                    Alleen grondwoord (T weg!)
                  </p>
                  <p className="font-mono text-xs bg-fuchsia-50 p-1 rounded text-center" data-id="element-735">
                    Loop jij?
                  </p>
                </div>
              </div>
            </div>

            {/* 't Kofschip - Visual */}
            <div className="bg-red-50 rounded-xl p-5 border-2 border-red-200" data-id="element-736">
              <h4 className="font-bold text-red-900 mb-3 text-lg" data-id="element-737">
                🚢 't Kofschip
              </h4>
              <div className="bg-white rounded-lg p-4 border border-red-200 text-center mb-3" data-id="element-738">
                <p className="text-2xl font-mono font-bold text-red-900 tracking-widest" data-id="element-739">
                  T K F S CH P
                </p>
                <p className="text-xs text-slate-500 mt-1" data-id="element-740">
                  Medeklinkers in de stam?
                </p>
              </div>
              <div className="grid grid-cols-2 gap-3" data-id="element-741">
                <div className="bg-green-100 p-3 rounded-lg text-center border border-green-200" data-id="element-742">
                  <div className="font-bold text-green-800 mb-1" data-id="element-743">JA</div>
                  <div className="text-sm text-green-900" data-id="element-744">+ te(n)</div>
                </div>
                <div className="bg-blue-100 p-3 rounded-lg text-center border border-blue-200" data-id="element-745">
                  <div className="font-bold text-blue-800 mb-1" data-id="element-746">NEE</div>
                  <div className="text-sm text-blue-900" data-id="element-747">+ de(n)</div>
                </div>
              </div>
            </div>

            {/* NEW: Onregelmatige Werkwoorden */}
            <div className="bg-orange-50 rounded-xl p-5 border-2 border-orange-200" data-id="element-748">
              <h4 className="font-bold text-orange-900 mb-3 text-lg" data-id="element-749">
                ⚠️ Onregelmatige Werkwoorden
              </h4>
              <div className="bg-white rounded-lg overflow-hidden border border-orange-200 text-sm" data-id="element-750">
                <table className="w-full text-left" data-id="element-751">
                  <thead className="bg-orange-100 text-orange-900" data-id="element-752">
                    <tr data-id="element-753">
                      <th className="p-2 font-bold" data-id="element-754">Nu</th>
                      <th className="p-2 font-bold" data-id="element-755">Toen</th>
                      <th className="p-2 font-bold" data-id="element-756">Gedaan</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-orange-100 text-slate-700" data-id="element-757">
                    <tr data-id="element-758">
                      <td className="p-2" data-id="element-759">schrijven</td>
                      <td className="p-2" data-id="element-760">schreef</td>
                      <td className="p-2" data-id="element-761">geschreven</td>
                    </tr>
                    <tr data-id="element-762">
                      <td className="p-2" data-id="element-763">eten</td>
                      <td className="p-2" data-id="element-764">at</td>
                      <td className="p-2" data-id="element-765">gegeten</td>
                    </tr>
                    <tr data-id="element-766">
                      <td className="p-2" data-id="element-767">lopen</td>
                      <td className="p-2" data-id="element-768">liep</td>
                      <td className="p-2" data-id="element-769">gelopen</td>
                    </tr>
                    <tr data-id="element-770">
                      <td className="p-2" data-id="element-771">zijn</td>
                      <td className="p-2" data-id="element-772">was</td>
                      <td className="p-2" data-id="element-773">geweest</td>
                    </tr>
                    <tr data-id="element-774">
                      <td className="p-2" data-id="element-775">hebben</td>
                      <td className="p-2" data-id="element-776">had</td>
                      <td className="p-2" data-id="element-777">gehad</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* NEW: Voltooid Deelwoord Stappenplan */}
            <div className="bg-indigo-50 rounded-xl p-5 border-2 border-indigo-200" data-id="element-778">
              <h4 className="font-bold text-indigo-900 mb-3 text-lg" data-id="element-779">
                🏁 Voltooid Deelwoord
              </h4>
              <div className="space-y-2" data-id="element-780">
                <div className="flex gap-3 bg-white p-2 rounded border border-indigo-100" data-id="element-781">
                  <div className="bg-indigo-100 text-indigo-700 font-bold w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-xs" data-id="element-782">
                    1
                  </div>
                  <div className="text-sm text-slate-700" data-id="element-783">
                    <span className="font-bold text-indigo-900" data-id="element-784">Stam:</span>{' '}
                    werkwoord - en
                  </div>
                </div>
                <div className="flex gap-3 bg-white p-2 rounded border border-indigo-100" data-id="element-785">
                  <div className="bg-indigo-100 text-indigo-700 font-bold w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-xs" data-id="element-786">
                    2
                  </div>
                  <div className="text-sm text-slate-700" data-id="element-787">
                    <span className="font-bold text-indigo-900" data-id="element-788">Check:</span> 't
                    kofschip?
                  </div>
                </div>
                <div className="flex gap-3 bg-white p-2 rounded border border-indigo-100" data-id="element-789">
                  <div className="bg-indigo-100 text-indigo-700 font-bold w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-xs" data-id="element-790">
                    3
                  </div>
                  <div className="text-sm text-slate-700" data-id="element-791">
                    <span className="font-bold text-indigo-900" data-id="element-792">Maak:</span> ge
                    + stam + t/d
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>;
    case 'SpellingRegel':
      return <>
          <div className="mb-6" data-id="element-793">
            <h3 className="text-2xl font-bold text-slate-900 mb-2 flex items-center gap-2" data-id="element-794">
              <span className="text-3xl" data-id="element-795">📖</span>
              Spellingregels
            </h3>
            <p className="text-slate-600" data-id="element-796">
              Belangrijke spellingregels voor groep 6
            </p>
          </div>

          <div className="space-y-6" data-id="element-797">
            {/* Weetwoorden - Cards */}
            <div className="bg-violet-50 rounded-xl p-5 border-2 border-violet-200" data-id="element-798">
              <h4 className="font-bold text-violet-900 mb-3 text-lg" data-id="element-799">
                📝 Weetwoorden
              </h4>
              <div className="space-y-3" data-id="element-800">
                <div className="bg-white p-3 rounded-lg border border-violet-200" data-id="element-801">
                  <p className="font-bold text-violet-900 text-sm mb-1" data-id="element-802">
                    é (café-woord)
                  </p>
                  <p className="text-xs text-slate-600" data-id="element-803">
                    café, logé (maar: idee, zee)
                  </p>
                </div>
                <div className="bg-white p-3 rounded-lg border border-violet-200" data-id="element-804">
                  <p className="font-bold text-violet-900 text-sm mb-1" data-id="element-805">
                    eau (cadeau-woord)
                  </p>
                  <p className="text-xs text-slate-600" data-id="element-806">
                    cadeau, bureau, niveau
                  </p>
                </div>
              </div>
            </div>

            {/* Klinkers - Visual Comparison */}
            <div className="bg-amber-50 rounded-xl p-5 border-2 border-amber-200" data-id="element-807">
              <h4 className="font-bold text-amber-900 mb-3 text-lg" data-id="element-808">
                🔤 Klinkers
              </h4>
              <div className="grid grid-cols-2 gap-3" data-id="element-809">
                <div className="bg-white p-3 rounded-lg border border-amber-200" data-id="element-810">
                  <p className="font-bold text-amber-900 text-sm mb-1" data-id="element-811">Kort</p>
                  <p className="text-xs text-slate-600 mb-1" data-id="element-812">
                    Dubbele medeklinker
                  </p>
                  <p className="font-mono text-xs bg-amber-50 p-1 rounded text-center" data-id="element-813">
                    pot → potten
                  </p>
                </div>
                <div className="bg-white p-3 rounded-lg border border-amber-200" data-id="element-814">
                  <p className="font-bold text-amber-900 text-sm mb-1" data-id="element-815">Lang</p>
                  <p className="text-xs text-slate-600 mb-1" data-id="element-816">
                    Enkele medeklinker
                  </p>
                  <p className="font-mono text-xs bg-amber-50 p-1 rounded text-center" data-id="element-817">
                    maan → manen
                  </p>
                </div>
              </div>
            </div>

            {/* C-Regel - Visual */}
            <div className="bg-purple-50 rounded-xl p-5 border-2 border-purple-200" data-id="element-818">
              <h4 className="font-bold text-purple-900 mb-3 text-lg" data-id="element-819">
                🅲 De C-Regel
              </h4>
              <div className="space-y-2" data-id="element-820">
                <div className="flex items-center gap-3 bg-white p-2 rounded border border-purple-100" data-id="element-821">
                  <span className="font-bold text-purple-700 w-8" data-id="element-822">K</span>
                  <span className="text-sm text-slate-600" data-id="element-823">
                    voor a, o, u (cola)
                  </span>
                </div>
                <div className="flex items-center gap-3 bg-white p-2 rounded border border-purple-100" data-id="element-824">
                  <span className="font-bold text-purple-700 w-8" data-id="element-825">S</span>
                  <span className="text-sm text-slate-600" data-id="element-826">
                    voor e, i (cent)
                  </span>
                </div>
              </div>
            </div>
          </div>
        </>;
    case 'Taalbegrip':
      return <>
          <div className="mb-6" data-id="element-827">
            <h3 className="text-2xl font-bold text-slate-900 mb-2 flex items-center gap-2" data-id="element-828">
              <span className="text-3xl" data-id="element-829">💬</span>
              Taalbegrip
            </h3>
            <p className="text-slate-600" data-id="element-830">Alles over woordsoorten en zinnen</p>
          </div>

          <div className="space-y-6" data-id="element-831">
            {/* NEW: Andere Woordsoorten */}
            <div className="bg-sky-50 rounded-xl p-5 border-2 border-sky-200" data-id="element-832">
              <h4 className="font-bold text-sky-900 mb-3 text-lg" data-id="element-833">
                📚 Woordsoorten
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-834">
                <div className="bg-white p-3 rounded-lg border border-sky-200" data-id="element-835">
                  <p className="font-bold text-sky-900 text-sm mb-1" data-id="element-836">
                    Zelfstandig Naamwoord
                  </p>
                  <p className="text-xs text-slate-600" data-id="element-837">
                    Mens, dier, ding (de tafel, een hond)
                  </p>
                </div>
                <div className="bg-white p-3 rounded-lg border border-sky-200" data-id="element-838">
                  <p className="font-bold text-sky-900 text-sm mb-1" data-id="element-839">
                    Werkwoord
                  </p>
                  <p className="text-xs text-slate-600" data-id="element-840">
                    Wat je doet (lopen, eten, zijn)
                  </p>
                </div>
                <div className="bg-white p-3 rounded-lg border border-sky-200" data-id="element-841">
                  <p className="font-bold text-sky-900 text-sm mb-1" data-id="element-842">
                    Bijvoeglijk Naamwoord
                  </p>
                  <p className="text-xs text-slate-600" data-id="element-843">
                    Hoe iets is (groot, mooi, rood)
                  </p>
                </div>
                <div className="bg-white p-3 rounded-lg border border-sky-200" data-id="element-844">
                  <p className="font-bold text-sky-900 text-sm mb-1" data-id="element-845">
                    Lidwoord
                  </p>
                  <p className="text-xs text-slate-600" data-id="element-846">de, het, een</p>
                </div>
              </div>
            </div>

            {/* NEW: Persoonsvorm Vinden */}
            <div className="bg-pink-50 rounded-xl p-5 border-2 border-pink-200" data-id="element-847">
              <h4 className="font-bold text-pink-900 mb-3 text-lg" data-id="element-848">
                🔍 Persoonsvorm Vinden
              </h4>
              <div className="space-y-2" data-id="element-849">
                <div className="flex gap-3 bg-white p-2 rounded border border-pink-100" data-id="element-850">
                  <div className="bg-pink-100 text-pink-700 font-bold w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-xs" data-id="element-851">
                    1
                  </div>
                  <div className="text-sm text-slate-700" data-id="element-852">
                    Zoek het werkwoord in de zin
                  </div>
                </div>
                <div className="flex gap-3 bg-white p-2 rounded border border-pink-100" data-id="element-853">
                  <div className="bg-pink-100 text-pink-700 font-bold w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-xs" data-id="element-854">
                    2
                  </div>
                  <div className="text-sm text-slate-700" data-id="element-855">
                    Verander de tijd (nu ↔ toen)
                  </div>
                </div>
                <div className="bg-pink-100/50 p-3 rounded-lg text-sm text-pink-900 mt-2" data-id="element-856">
                  <p className="font-bold mb-1" data-id="element-857">Voorbeeld:</p>
                  <p data-id="element-858">
                    'De hond <span className="font-bold underline" data-id="element-859">blaft</span>'
                    → 'De hond{' '}
                    <span className="font-bold underline" data-id="element-860">blafte</span>'
                  </p>
                  <p className="text-xs mt-1 text-pink-700" data-id="element-861">
                    Blaft verandert → persoonsvorm!
                  </p>
                </div>
              </div>
            </div>

            {/* Stoffelijk Bijvoeglijk Naamwoord */}
            <div className="bg-purple-50 rounded-xl p-5 border-2 border-purple-200" data-id="element-862">
              <h4 className="font-bold text-purple-900 mb-3 text-lg" data-id="element-863">
                🪵 Stoffelijk B.N.
              </h4>
              <div className="bg-white rounded-lg p-4 border border-purple-200 mb-3" data-id="element-864">
                <p className="text-sm text-slate-700 mb-2" data-id="element-865">
                  Waar is het van gemaakt?
                </p>
                <div className="flex flex-wrap gap-2" data-id="element-866">
                  <span className="bg-purple-100 text-purple-800 text-xs px-2 py-1 rounded font-bold" data-id="element-867">
                    hout → houten
                  </span>
                  <span className="bg-purple-100 text-purple-800 text-xs px-2 py-1 rounded font-bold" data-id="element-868">
                    goud → gouden
                  </span>
                  <span className="bg-purple-100 text-purple-800 text-xs px-2 py-1 rounded font-bold" data-id="element-869">
                    steen → stenen
                  </span>
                </div>
              </div>
              <RuleCard title="Let Op!" content="Gebruik het stoffelijke woord, niet de stof zelf. 'Een gouden ring', niet 'een goud ring'." icon="alert" data-id="element-870" />
            </div>

            {/* Herkennen - Interactive Question */}
            <div className="bg-indigo-50 rounded-xl p-5 border-2 border-indigo-200" data-id="element-871">
              <h4 className="font-bold text-indigo-900 mb-3 text-lg" data-id="element-872">
                🔍 Hoe herken je het?
              </h4>
              <div className="bg-white rounded-lg p-4 border border-indigo-200" data-id="element-873">
                <p className="font-bold text-indigo-900 mb-2 text-sm" data-id="element-874">
                  Stel de vraag:
                </p>
                <p className="text-lg text-center font-medium text-slate-800 py-2" data-id="element-875">
                  "Van welke stof is het gemaakt?"
                </p>
                <div className="mt-2 text-xs text-slate-500 text-center" data-id="element-876">
                  Als je die vraag kunt beantwoorden, is het stoffelijk!
                </div>
              </div>
            </div>
          </div>
        </>;
    default:
      return <div className="text-center text-slate-500 py-12" data-id="element-877">
          <Lightbulb className="w-12 h-12 mx-auto mb-4 opacity-50" data-id="element-878" />
          <p data-id="element-879">Geen spiekbriefje beschikbaar voor deze module.</p>
        </div>;
  }
}